import { _ as _define_property } from "@swc/helpers/_/_define_property";
import Decimal from 'decimal.js';
import { sendErrReport } from '~/infra/utils';
import { accountingFullName } from '~/integrations/Accounting/accounting-names';
import * as serviceBillingModel from '~/models/service-billing';
import * as serviceCostModel from '~/models/service-cost';
import { Accounting } from '~/types/accounting';
import { MoneyModel } from './MoneyModel';
import { ServiceCostType } from './service-cost/model';
import { formatDateLong, formatDateLongOrdinal } from './time/selectors';
export var ActivityLogEntryType = /*#__PURE__*/ function(ActivityLogEntryType) {
    ActivityLogEntryType["Relationship"] = "relationship";
    ActivityLogEntryType["Charge"] = "charge";
    ActivityLogEntryType["Credit"] = "credit";
    ActivityLogEntryType["Invoice"] = "invoice";
    ActivityLogEntryType["Service"] = "service";
    ActivityLogEntryType["Business"] = "business";
    ActivityLogEntryType["SentNotification"] = "sentNotification";
    return ActivityLogEntryType;
}({});
const INVOICE_NOTIFICATION_TYPES = new Set([
    'InvoiceIssued',
    'PaymentFailedCustomer',
    'InvoiceRefundedCustomer',
    'AdHocInvoiceIssued',
    'AdHocInvoiceOverdueReminderCustomer'
]);
export const isInvoiceNotification = (entry)=>INVOICE_NOTIFICATION_TYPES.has(entry.state);
export var ActivityLogAgreementState = /*#__PURE__*/ function(ActivityLogAgreementState) {
    ActivityLogAgreementState["Sent"] = "sent";
    ActivityLogAgreementState["Reviewed"] = "reviewed";
    ActivityLogAgreementState["MicroDepositStarted"] = "microDepositStarted";
    ActivityLogAgreementState["MicroDepositStartedV2"] = "microDepositStartedV2";
    ActivityLogAgreementState["MicroDeposit"] = "microDeposit";
    ActivityLogAgreementState["MicroDepositFailed"] = "microDepositFailed";
    ActivityLogAgreementState["MicroDepositFailedV2"] = "microDepositFailedV2";
    ActivityLogAgreementState["MicroDepositVerified"] = "microDepositVerified";
    ActivityLogAgreementState["MicroDepositVerifiedV2"] = "microDepositVerifiedV2";
    ActivityLogAgreementState["PaymentMethod"] = "paymentMethod";
    ActivityLogAgreementState["Approved"] = "approved";
    ActivityLogAgreementState["Terminated"] = "terminated";
    ActivityLogAgreementState["Changed"] = "changed";
    ActivityLogAgreementState["ChangesApproved"] = "changesApproved";
    ActivityLogAgreementState["ChangesAutoApproved"] = "changesAutoApproved";
    ActivityLogAgreementState["StoppedAutoApproval"] = "stoppedAutoApproval";
    ActivityLogAgreementState["SigningReminderSent"] = "signingReminderSent";
    ActivityLogAgreementState["SigningReminderSentManual"] = "signingReminderSentManual";
    ActivityLogAgreementState["MicroDepositReminderSent"] = "microDepositReminderSent";
    ActivityLogAgreementState["PaymentMethodChanged"] = "paymentMethodChanged";
    ActivityLogAgreementState["SignedExternally"] = "signedExternally";
    ActivityLogAgreementState["RequestedPaymentMethod"] = "requestedPaymentMethod";
    ActivityLogAgreementState["LegalTermsUpdated"] = "legalTermsUpdated";
    ActivityLogAgreementState["AmendmentsApprovalMethodUpdated"] = "amendmentsApprovalMethodUpdated";
    ActivityLogAgreementState["InvoicesApprovalMethodUpdated"] = "invoicesApprovalMethodUpdated";
    ActivityLogAgreementState["TitleUpdated"] = "titleUpdated";
    ActivityLogAgreementState["ManualPaymentsUpdated"] = "manualPaymentsUpdated";
    ActivityLogAgreementState["CoverCreditCardFeesUpdated"] = "coverCreditCardFeesUpdated";
    ActivityLogAgreementState["SellerUpdated"] = "sellerUpdated";
    ActivityLogAgreementState["PracticeManagementContactCreated"] = "practiceManagementContactCreated";
    ActivityLogAgreementState["PracticeManagementItemsCreated"] = "practiceManagementItemsCreated";
    ActivityLogAgreementState["PaymentMethodDisconnected"] = "paymentMethodDisconnected";
    ActivityLogAgreementState["SentSilently"] = "sentSilently";
    ActivityLogAgreementState["MicroDepositRestarted"] = "microDepositRestarted";
    ActivityLogAgreementState["AmendmentApprovalReminderSent"] = "amendmentApprovalReminderSent";
    ActivityLogAgreementState["WithdrawnForEditing"] = "withdrawnForEditing";
    ActivityLogAgreementState["Republished"] = "republished";
    ActivityLogAgreementState["ServiceTermsUpdated"] = "serviceTermsUpdated";
    ActivityLogAgreementState["ServiceTermsRemoved"] = "serviceTermsRemoved";
    ActivityLogAgreementState["ReferenceFieldChanged"] = "referenceFieldChanged";
    ActivityLogAgreementState["ReferenceFieldChangedInternal"] = "referenceFieldChangedInternal";
    ActivityLogAgreementState["AmendmentDraftSaved"] = "amendmentDraftSaved";
    ActivityLogAgreementState["AmendmentDraftDiscarded"] = "amendmentDraftDiscarded";
    return ActivityLogAgreementState;
}({});
export var ActivityLogChannel = /*#__PURE__*/ function(ActivityLogChannel) {
    ActivityLogChannel["Email"] = "email";
    ActivityLogChannel["Sms"] = "sms";
    return ActivityLogChannel;
}({});
export var ActivityLogChargeState = /*#__PURE__*/ function(ActivityLogChargeState) {
    ActivityLogChargeState["Created"] = "created";
    ActivityLogChargeState["Canceled"] = "canceled";
    ActivityLogChargeState["Approved"] = "approved";
    return ActivityLogChargeState;
}({});
export var ActivityLogCreditState = /*#__PURE__*/ function(ActivityLogCreditState) {
    ActivityLogCreditState["Added"] = "creditAdded";
    ActivityLogCreditState["Paused"] = "creditPaused";
    ActivityLogCreditState["Resumed"] = "creditResumed";
    ActivityLogCreditState["Canceled"] = "creditCanceled";
    return ActivityLogCreditState;
}({});
export var ActivityLogInvoiceState = /*#__PURE__*/ function(ActivityLogInvoiceState) {
    ActivityLogInvoiceState["AccountingInvoiceResyncRequested"] = "accountingInvoiceResyncRequested";
    ActivityLogInvoiceState["AccountingInvoiceSynced"] = "accountingInvoiceSynced";
    ActivityLogInvoiceState["AccountingPaymentSynced"] = "accountingPaymentSynced";
    ActivityLogInvoiceState["Voided"] = "voided";
    ActivityLogInvoiceState["Unvoided"] = "unvoided";
    ActivityLogInvoiceState["Completed"] = "completed";
    ActivityLogInvoiceState["IssuedIncomplete"] = "issuedIncomplete";
    ActivityLogInvoiceState["Issued"] = "issued";
    ActivityLogInvoiceState["PaymentFailed"] = "paymentFailed";
    ActivityLogInvoiceState["PaymentRetried"] = "paymentRetried";
    ActivityLogInvoiceState["PaymentRetriedByAdmin"] = "paymentRetriedByAdmin";
    ActivityLogInvoiceState["ApprovalRevertedByAdmin"] = "approvalRevertedByAdmin";
    ActivityLogInvoiceState["PaymentAutoRetried"] = "paymentAutoRetried";
    ActivityLogInvoiceState["PaymentRetriedByCustomer"] = "paymentRetriedByCustomer";
    ActivityLogInvoiceState["PaymentRetriedByVendor"] = "paymentRetriedByVendor";
    ActivityLogInvoiceState["Paid"] = "paid";
    ActivityLogInvoiceState["PaidExternally"] = "paidExternally";
    ActivityLogInvoiceState["DisputeResolved"] = "disputeResolved";
    ActivityLogInvoiceState["Approved"] = "approved";
    ActivityLogInvoiceState["Resent"] = "resent";
    ActivityLogInvoiceState["PaymentConfirmed"] = "paymentConfirmed";
    ActivityLogInvoiceState["PaymentProcessing"] = "paymentProcessing";
    ActivityLogInvoiceState["InvoiceIntegrationCreated"] = "invoiceIntegrationCreated";
    ActivityLogInvoiceState["InvoiceIntegrationSyncFailed"] = "invoiceIntegrationSyncFailed";
    ActivityLogInvoiceState["InvoiceIntegrationPaymentCreated"] = "invoiceIntegrationPaymentCreated";
    ActivityLogInvoiceState["InvoiceIntegrationPaymentSyncFailed"] = "invoiceIntegrationPaymentSyncFailed";
    ActivityLogInvoiceState["InvoiceIntegrationVoided"] = "invoiceIntegrationVoided";
    ActivityLogInvoiceState["Refunded"] = "refunded";
    ActivityLogInvoiceState["PaymentStoppedOLD"] = "disputed";
    ActivityLogInvoiceState["PaymentStopped"] = "paymentStopped";
    ActivityLogInvoiceState["PaymentResumed"] = "paymentResumed";
    ActivityLogInvoiceState["PaymentDisputed"] = "paymentDisputed";
    ActivityLogInvoiceState["PaymentDisputeWon"] = "paymentDisputeWon";
    ActivityLogInvoiceState["RefundRequested"] = "refundRequested";
    ActivityLogInvoiceState["PaymentAutoApproved"] = "paymentAutoApproved";
    ActivityLogInvoiceState["AutoApprovalDisabled"] = "autoApprovalDisabled";
    ActivityLogInvoiceState["PayNowRequested"] = "payNowRequested";
    ActivityLogInvoiceState["RefundFailed"] = "refundFailed";
    ActivityLogInvoiceState["InvoiceSanctionFound"] = "invoiceSanctionFound";
    ActivityLogInvoiceState["UnmarkedAsPaidExternally"] = "unmarkedAsPaidExternally";
    ActivityLogInvoiceState["InvoiceAttachmentsAdded"] = "invoiceAttachmentsAdded";
    ActivityLogInvoiceState["InvoiceAttachmentRemoved"] = "invoiceAttachmentRemoved";
    ActivityLogInvoiceState["AdHocInvoiceIssued"] = "adHocInvoiceIssued";
    ActivityLogInvoiceState["AdHocInvoiceScheduled"] = "adHocInvoiceScheduled";
    ActivityLogInvoiceState["AdHocInvoiceApproved"] = "adHocInvoiceApproved";
    ActivityLogInvoiceState["AdHocInvoiceOverdueReminderSent"] = "adHocInvoiceOverdueReminderSent";
    ActivityLogInvoiceState["AdHocInvoiceSharedByLink"] = "adHocInvoiceSharedByLink";
    return ActivityLogInvoiceState;
}({});
export var ActivityLogAdHocInvoiceNotifyType = /*#__PURE__*/ function(ActivityLogAdHocInvoiceNotifyType) {
    ActivityLogAdHocInvoiceNotifyType["Notification"] = "notification";
    ActivityLogAdHocInvoiceNotifyType["Silent"] = "silent";
    return ActivityLogAdHocInvoiceNotifyType;
}({});
export var ActivityLogServiceState = /*#__PURE__*/ function(ActivityLogServiceState) {
    ActivityLogServiceState["Created"] = "created";
    ActivityLogServiceState["Changed"] = "changed";
    ActivityLogServiceState["Approved"] = "approved";
    ActivityLogServiceState["AutoApproved"] = "autoApproved";
    ActivityLogServiceState["ChangesApproved"] = "changesApproved";
    ActivityLogServiceState["ChangesAutoApproved"] = "changesAutoApproved";
    ActivityLogServiceState["Paused"] = "paused";
    ActivityLogServiceState["Resumed"] = "resumed";
    ActivityLogServiceState["Terminated"] = "terminated";
    ActivityLogServiceState["Canceled"] = "canceled";
    ActivityLogServiceState["BillCommandDeliverablesPendingApproval"] = "billCommandDeliverablesPendingApproval";
    ActivityLogServiceState["BillCommandDeliverablesApproved"] = "billCommandDeliverablesApproved";
    ActivityLogServiceState["BillCommandDeliverablesRejected"] = "billCommandDeliverablesRejected";
    ActivityLogServiceState["BillCommandDeliverablesWithdrawn"] = "billCommandDeliverablesWithdrawn";
    ActivityLogServiceState["ChangesDiscarded"] = "changesDiscarded";
    ActivityLogServiceState["PriceIncreased"] = "autoPriceIncreased";
    ActivityLogServiceState["IntegratedClassChanged"] = "integratedClassChanged";
    ActivityLogServiceState["ServiceDiscountExpired"] = "serviceDiscountExpired";
    return ActivityLogServiceState;
}({});
export var ActivityLogBusinessState = /*#__PURE__*/ function(ActivityLogBusinessState) {
    ActivityLogBusinessState["BusinessNameChanged"] = "businessNameChanged";
    ActivityLogBusinessState["CustomerNameChangedBySeller"] = "customerNameChangedBySeller";
    ActivityLogBusinessState["TeamMemberAdded"] = "teamMemberAdded";
    ActivityLogBusinessState["TeamMemberAddedBySeller"] = "teamMemberAddedBySeller";
    ActivityLogBusinessState["TeamMemberRemoved"] = "teamMemberRemoved";
    ActivityLogBusinessState["TeamMemberRemovedBySeller"] = "teamMemberRemovedBySeller";
    ActivityLogBusinessState["TeamMemberRoleChanged"] = "teamMemberRoleChanged";
    ActivityLogBusinessState["TeamMemberRoleChangedBySeller"] = "teamMemberRoleChangedBySeller";
    ActivityLogBusinessState["CustomerBusinessCreated"] = "customerBusinessCreated";
    ActivityLogBusinessState["CustomerAddressChanged"] = "customerAddressChanged";
    return ActivityLogBusinessState;
}({});
export var ActivityLogHistoricUserRole = /*#__PURE__*/ function(ActivityLogHistoricUserRole) {
    ActivityLogHistoricUserRole["Accountant"] = "accountant";
    return ActivityLogHistoricUserRole;
}({});
export var ActivityLogParty = /*#__PURE__*/ function(ActivityLogParty) {
    ActivityLogParty["Customer"] = "customer";
    ActivityLogParty["Vendor"] = "vendor";
    return ActivityLogParty;
}({});
export var ActivityLogPaymentMethodType = /*#__PURE__*/ function(ActivityLogPaymentMethodType) {
    ActivityLogPaymentMethodType["MultiAch"] = "multiAch";
    ActivityLogPaymentMethodType["Ach"] = "ach";
    ActivityLogPaymentMethodType["CreditCard"] = "creditCard";
    return ActivityLogPaymentMethodType;
}({});
export var ActivityLogBusinessDataType = /*#__PURE__*/ function(ActivityLogBusinessDataType) {
    ActivityLogBusinessDataType["BusinessNameChanged"] = "businessNameChanged";
    ActivityLogBusinessDataType["CustomerNameChangedBySeller"] = "customerNameChangedBySeller";
    ActivityLogBusinessDataType["TeamMemberAdded"] = "teamMemberAdded";
    ActivityLogBusinessDataType["TeamMemberAddedBySeller"] = "teamMemberAddedBySeller";
    ActivityLogBusinessDataType["TeamMemberRemoved"] = "teamMemberRemoved";
    ActivityLogBusinessDataType["TeamMemberRemovedBySeller"] = "teamMemberRemovedBySeller";
    ActivityLogBusinessDataType["TeamMemberRoleChanged"] = "teamMemberRoleChanged";
    ActivityLogBusinessDataType["TeamMemberRoleChangedBySeller"] = "teamMemberRoleChangedBySeller";
    ActivityLogBusinessDataType["CustomerBusinessCreated"] = "customerBusinessCreated";
    ActivityLogBusinessDataType["CustomerAddressChanged"] = "customerAddressChanged";
    return ActivityLogBusinessDataType;
}({});
export var ActivityLogPMIntegrationName = /*#__PURE__*/ function(ActivityLogPMIntegrationName) {
    ActivityLogPMIntegrationName["Karbon"] = "karbon";
    ActivityLogPMIntegrationName["ClientHub"] = "client-hub";
    ActivityLogPMIntegrationName["FinancialCents"] = "financial-cents";
    ActivityLogPMIntegrationName["Keeper"] = "keeper";
    return ActivityLogPMIntegrationName;
}({});
export var AccountingIntegrationType = /*#__PURE__*/ function(AccountingIntegrationType) {
    AccountingIntegrationType["QBO"] = "qbo";
    AccountingIntegrationType["Xero"] = "xero";
    return AccountingIntegrationType;
}({});
const RECOGNIZED_ENTRY_TYPES = new Set(Object.values(ActivityLogEntryType));
export class ActivityLogEntryModel {
    static make(dto) {
        const entryType = dto.type;
        switch(dto.type){
            case "relationship":
                return new ActivityLogAgreementEntryModel(dto);
            case "charge":
                return new ActivityLogChargeEntryModel(dto);
            case "credit":
                return new ActivityLogCreditEntryModel(dto);
            case "invoice":
                return new ActivityLogInvoiceEntryModel(dto);
            case "service":
                return new ActivityLogServiceEntryModel(dto);
            case "business":
                return ActivityLogBusinessEntryModel.make(dto);
            case "sentNotification":
                return new ActivityLogSentNotificationEntryModel(dto);
            default:
                sendErrReport(new Error("Unknown entry type ".concat(entryType)));
                return new ActivityLogEntryModel(dto);
        }
    }
    isAgreementEntry() {
        return this.dto.type === "relationship";
    }
    isServiceEntry() {
        return this.dto.type === "service";
    }
    isChargeEntry() {
        return this.dto.type === "charge";
    }
    isCreditEntry() {
        return this.dto.type === "credit";
    }
    isInvoiceEntry() {
        return this.dto.type === "invoice";
    }
    isBusinessEntry() {
        return this.dto.type === "business";
    }
    isSentNotificationEntry() {
        return this.dto.type === "sentNotification";
    }
    isUnrecognizedEntry() {
        return !RECOGNIZED_ENTRY_TYPES.has(this.dto.type);
    }
    id() {
        return this.dto.id;
    }
    state() {
        return this.dto.state;
    }
    timestamp() {
        return this.timestampAsDate;
    }
    accept(visitor) {
        if (this.isAgreementEntry()) {
            return visitor.whenAgreement(this);
        } else if (this.isChargeEntry()) {
            return visitor.whenCharge(this);
        } else if (this.isCreditEntry()) {
            return visitor.whenCredit(this);
        } else if (this.isInvoiceEntry()) {
            return visitor.whenInvoice(this);
        } else if (this.isServiceEntry()) {
            return visitor.whenService(this);
        } else if (this.isBusinessEntry()) {
            return visitor.whenBusiness(this);
        } else if (this.isSentNotificationEntry()) {
            return visitor.whenSentNotification(this);
        } else {
            return null;
        }
    }
    nameAndEmail(name, email) {
        return name ? email ? "".concat(name, " (").concat(email, ")") : name : '';
    }
    constructor(dto){
        _define_property(this, "dto", void 0);
        _define_property(this, "timestampAsDate", void 0);
        this.dto = dto;
        this.timestampAsDate = new Date(this.dto.timestamp);
    }
}
export class ActivityLogAgreementEntryModel extends ActivityLogEntryModel {
    state() {
        return this.agreementDTO.state;
    }
    vendorName() {
        var _this_agreementDTO_data_vendor;
        return (_this_agreementDTO_data_vendor = this.agreementDTO.data.vendor) === null || _this_agreementDTO_data_vendor === void 0 ? void 0 : _this_agreementDTO_data_vendor.name;
    }
    customerName() {
        var _this_agreementDTO_data_customer;
        return (_this_agreementDTO_data_customer = this.agreementDTO.data.customer) === null || _this_agreementDTO_data_customer === void 0 ? void 0 : _this_agreementDTO_data_customer.name;
    }
    customer() {
        return this.agreementDTO.data.customer;
    }
    vendor() {
        return this.agreementDTO.data.vendor;
    }
    partyType() {
        return this.agreementDTO.data.partyType;
    }
    referenceFieldsUpdated() {
        var _this_agreementDTO_data_referenceFieldsUpdated;
        return (_this_agreementDTO_data_referenceFieldsUpdated = this.agreementDTO.data.referenceFieldsUpdated) !== null && _this_agreementDTO_data_referenceFieldsUpdated !== void 0 ? _this_agreementDTO_data_referenceFieldsUpdated : [];
    }
    customerNameAndEmail() {
        const customer = this.customer();
        return customer ? this.nameAndEmail(customer.name, customer.email) : '';
    }
    vendorNameAndEmail() {
        const vendor = this.vendor();
        return vendor ? this.nameAndEmail(vendor.name, vendor.email) : '';
    }
    isReminderEntry() {
        return [
            "signingReminderSent",
            "signingReminderSentManual",
            "microDepositReminderSent"
        ].includes(this.agreementDTO.state);
    }
    isDeletionEntry() {
        return [
            "amendmentDraftDiscarded"
        ].includes(this.agreementDTO.state);
    }
    isSaveEntry() {
        return [
            "amendmentDraftSaved"
        ].includes(this.agreementDTO.state);
    }
    isReferenceFieldsEntry() {
        return [
            "referenceFieldChanged",
            "referenceFieldChangedInternal"
        ].includes(this.agreementDTO.state);
    }
    channel() {
        return this.agreementDTO.data.channel;
    }
    paymentMethod() {
        return this.agreementDTO.data.paymentMethod;
    }
    hasReviewers() {
        const reviewers = this.reviewers();
        return !!reviewers && reviewers.length > 0;
    }
    reviewerNamesAndEmails() {
        const reviewers = this.reviewers();
        return (reviewers || []).map((r)=>this.nameAndEmail(r.name, r.email));
    }
    netTermsChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.netTerms;
    }
    amendmentsApprovalMethodChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.amendmentsApprovalMethod;
    }
    invoicesApprovalMethodChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.invoicesApprovalMethod;
    }
    titleChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.title;
    }
    manualPaymentsChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.manualPayments;
    }
    coverCCFeesChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.coverCreditCardFees;
    }
    sellerChanges() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.seller;
    }
    serviceTermsUpdatedChange() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.serviceTermsUpdated;
    }
    serviceTermsRemovedChange() {
        var _this_agreementDTO_data_changes;
        return (_this_agreementDTO_data_changes = this.agreementDTO.data.changes) === null || _this_agreementDTO_data_changes === void 0 ? void 0 : _this_agreementDTO_data_changes.serviceTermsRemoved;
    }
    integrationName() {
        return this.agreementDTO.data.integrationName;
    }
    integrationServices() {
        return this.agreementDTO.data.integrationServices;
    }
    reviewers() {
        return this.agreementDTO.data.reviewers;
    }
    constructor(dto){
        super(dto), _define_property(this, "agreementDTO", void 0);
        this.agreementDTO = dto;
    }
}
export class ActivityLogServiceEntryModel extends ActivityLogEntryModel {
    vendorName() {
        var _this_serviceDTO_data_vendor;
        return (_this_serviceDTO_data_vendor = this.serviceDTO.data.vendor) === null || _this_serviceDTO_data_vendor === void 0 ? void 0 : _this_serviceDTO_data_vendor.name;
    }
    customerName() {
        var _this_serviceDTO_data_customer;
        return (_this_serviceDTO_data_customer = this.serviceDTO.data.customer) === null || _this_serviceDTO_data_customer === void 0 ? void 0 : _this_serviceDTO_data_customer.name;
    }
    customer() {
        return this.serviceDTO.data.customer;
    }
    vendor() {
        return this.serviceDTO.data.vendor;
    }
    hasChanges() {
        return !!this.serviceDTO.data.service.changes;
    }
    descriptionChange() {
        var _this_serviceDTO_data_service_changes;
        return (_this_serviceDTO_data_service_changes = this.serviceDTO.data.service.changes) === null || _this_serviceDTO_data_service_changes === void 0 ? void 0 : _this_serviceDTO_data_service_changes.description;
    }
    termsChange() {
        var _this_serviceDTO_data_service_changes;
        return (_this_serviceDTO_data_service_changes = this.serviceDTO.data.service.changes) === null || _this_serviceDTO_data_service_changes === void 0 ? void 0 : _this_serviceDTO_data_service_changes.terms;
    }
    serviceChangesCost() {
        return this._serviceChangesCost;
    }
    priceChange() {
        const cost = this.serviceChangesCost();
        if (!(cost === null || cost === void 0 ? void 0 : cost.pricingType)) return null;
        switch(cost.pricingType){
            case ServiceCostType.Fixed:
            case ServiceCostType.Variable:
                return cost.price;
            case ServiceCostType.Range:
                return cost.maxPrice;
        }
    }
    priceBeforeChange() {
        var _this__serviceBeforeChangesCost;
        if (!((_this__serviceBeforeChangesCost = this._serviceBeforeChangesCost) === null || _this__serviceBeforeChangesCost === void 0 ? void 0 : _this__serviceBeforeChangesCost.pricingType)) return null;
        switch(this._serviceBeforeChangesCost.pricingType){
            case ServiceCostType.Fixed:
            case ServiceCostType.Variable:
                return this._serviceBeforeChangesCost.price;
            case ServiceCostType.Range:
                return this._serviceBeforeChangesCost.maxPrice;
        }
    }
    integratedClassNameChange() {
        var _this_serviceDTO_data_service_changes_integratedClass, _this_serviceDTO_data_service_changes;
        return ((_this_serviceDTO_data_service_changes = this.serviceDTO.data.service.changes) === null || _this_serviceDTO_data_service_changes === void 0 ? void 0 : (_this_serviceDTO_data_service_changes_integratedClass = _this_serviceDTO_data_service_changes.integratedClass) === null || _this_serviceDTO_data_service_changes_integratedClass === void 0 ? void 0 : _this_serviceDTO_data_service_changes_integratedClass.name) || null;
    }
    integratedClassNameBeforeChange() {
        var _this_serviceDTO_data_service_beforeChanges_integratedClass, _this_serviceDTO_data_service_beforeChanges;
        return ((_this_serviceDTO_data_service_beforeChanges = this.serviceDTO.data.service.beforeChanges) === null || _this_serviceDTO_data_service_beforeChanges === void 0 ? void 0 : (_this_serviceDTO_data_service_beforeChanges_integratedClass = _this_serviceDTO_data_service_beforeChanges.integratedClass) === null || _this_serviceDTO_data_service_beforeChanges_integratedClass === void 0 ? void 0 : _this_serviceDTO_data_service_beforeChanges_integratedClass.name) || null;
    }
    billingChange() {
        return this._serviceChangesBilling;
    }
    note() {
        return this.serviceDTO.data.service.note;
    }
    name() {
        return this.serviceDTO.data.service.name;
    }
    isDiscountExpirationEntry() {
        return this.state() === "serviceDiscountExpired";
    }
    constructor(dto){
        super(dto), _define_property(this, "serviceDTO", void 0), _define_property(this, "_serviceChangesCost", void 0), _define_property(this, "_serviceChangesBilling", void 0), _define_property(this, "_serviceBeforeChangesCost", void 0);
        this.serviceDTO = dto;
        const serviceChanges = dto.data.service.changes;
        if (serviceChanges === null || serviceChanges === void 0 ? void 0 : serviceChanges.cost) {
            this._serviceChangesCost = serviceCostModel.fromDTO(serviceChanges.cost);
        }
        if (serviceChanges === null || serviceChanges === void 0 ? void 0 : serviceChanges.billing) {
            this._serviceChangesBilling = serviceBillingModel.fromDTO(serviceChanges.billing) || undefined;
        }
        const serviceBeforeChanges = dto.data.service.beforeChanges;
        if (serviceBeforeChanges === null || serviceBeforeChanges === void 0 ? void 0 : serviceBeforeChanges.cost) {
            this._serviceBeforeChangesCost = serviceCostModel.fromDTO(serviceBeforeChanges.cost);
        }
    }
}
export class ActivityLogChargeEntryModel extends ActivityLogEntryModel {
    vendorName() {
        var _this_chargeDTO_data_vendor;
        return (_this_chargeDTO_data_vendor = this.chargeDTO.data.vendor) === null || _this_chargeDTO_data_vendor === void 0 ? void 0 : _this_chargeDTO_data_vendor.name;
    }
    customerName() {
        var _this_chargeDTO_data_customer;
        return (_this_chargeDTO_data_customer = this.chargeDTO.data.customer) === null || _this_chargeDTO_data_customer === void 0 ? void 0 : _this_chargeDTO_data_customer.name;
    }
    customer() {
        return this.chargeDTO.data.customer;
    }
    vendor() {
        return this.chargeDTO.data.vendor;
    }
    name() {
        return this.chargeDTO.data.charge.name;
    }
    note() {
        return this.chargeDTO.data.charge.note;
    }
    formattedPrice() {
        return this.price.display();
    }
    constructor(dto){
        super(dto), _define_property(this, "chargeDTO", void 0), _define_property(this, "price", void 0);
        this.chargeDTO = dto;
        this.price = MoneyModel.fromDTO(dto.data.charge.temp_price);
    }
}
export class ActivityLogCreditEntryModel extends ActivityLogEntryModel {
    state() {
        return this.creditDTO.state;
    }
    vendorName() {
        var _this_creditDTO_data_vendor;
        return (_this_creditDTO_data_vendor = this.creditDTO.data.vendor) === null || _this_creditDTO_data_vendor === void 0 ? void 0 : _this_creditDTO_data_vendor.name;
    }
    customerName() {
        var _this_creditDTO_data_customer;
        return (_this_creditDTO_data_customer = this.creditDTO.data.customer) === null || _this_creditDTO_data_customer === void 0 ? void 0 : _this_creditDTO_data_customer.name;
    }
    name() {
        return this.creditDTO.data.credit.name;
    }
    note() {
        return this.creditDTO.data.credit.note;
    }
    formattedAmount() {
        return this.amount.display();
    }
    constructor(dto){
        super(dto), _define_property(this, "creditDTO", void 0), _define_property(this, "amount", void 0);
        this.creditDTO = dto;
        this.amount = MoneyModel.fromDTO(dto.data.credit.amount);
    }
}
export class ActivityLogInvoiceEntryModel extends ActivityLogEntryModel {
    vendorName() {
        var _this_invoiceDTO_data_vendor;
        return (_this_invoiceDTO_data_vendor = this.invoiceDTO.data.vendor) === null || _this_invoiceDTO_data_vendor === void 0 ? void 0 : _this_invoiceDTO_data_vendor.name;
    }
    customerName() {
        var _this_invoiceDTO_data_customer;
        return (_this_invoiceDTO_data_customer = this.invoiceDTO.data.customer) === null || _this_invoiceDTO_data_customer === void 0 ? void 0 : _this_invoiceDTO_data_customer.name;
    }
    customer() {
        return this.invoiceDTO.data.customer;
    }
    vendor() {
        return this.invoiceDTO.data.vendor;
    }
    invoiceId() {
        return this.invoiceDTO.data.invoice.id;
    }
    integrationFullName() {
        const integrationType = this.integrationType();
        return integrationType && accountingFullName[integrationType];
    }
    integrationType() {
        const accountingType = {
            ["qbo"]: Accounting.QBO,
            ["xero"]: Accounting.Xero
        };
        return this.invoiceDTO.data.integrationName ? accountingType[this.invoiceDTO.data.integrationName] : undefined;
    }
    formattedNumber() {
        return this.invoiceDTO.data.invoice.formattedNumber;
    }
    disputeWonDate() {
        return formatDateLong(this.timestamp());
    }
    vendorBusinessName() {
        var _ref;
        var _this_invoiceDTO_data_vendor;
        return (_ref = (_this_invoiceDTO_data_vendor = this.invoiceDTO.data.vendor) === null || _this_invoiceDTO_data_vendor === void 0 ? void 0 : _this_invoiceDTO_data_vendor.businessName) !== null && _ref !== void 0 ? _ref : '';
    }
    totalAmount() {
        return this.totalAmountAsMoney;
    }
    paymentStoppedReason() {
        var _this_invoiceDTO_data_paymentStopped;
        return (_this_invoiceDTO_data_paymentStopped = this.invoiceDTO.data.paymentStopped) === null || _this_invoiceDTO_data_paymentStopped === void 0 ? void 0 : _this_invoiceDTO_data_paymentStopped.reason;
    }
    refundRequestedReason() {
        var _this_invoiceDTO_data_refundRequested, _this_invoiceDTO_data_refunded;
        return ((_this_invoiceDTO_data_refundRequested = this.invoiceDTO.data.refundRequested) === null || _this_invoiceDTO_data_refundRequested === void 0 ? void 0 : _this_invoiceDTO_data_refundRequested.reason) || ((_this_invoiceDTO_data_refunded = this.invoiceDTO.data.refunded) === null || _this_invoiceDTO_data_refunded === void 0 ? void 0 : _this_invoiceDTO_data_refunded.reason);
    }
    totalRefundedAmount() {
        var _this_invoiceDTO_data_refunded_amounts, _this_invoiceDTO_data_refunded;
        return new MoneyModel(new Decimal(((_this_invoiceDTO_data_refunded = this.invoiceDTO.data.refunded) === null || _this_invoiceDTO_data_refunded === void 0 ? void 0 : (_this_invoiceDTO_data_refunded_amounts = _this_invoiceDTO_data_refunded.amounts) === null || _this_invoiceDTO_data_refunded_amounts === void 0 ? void 0 : _this_invoiceDTO_data_refunded_amounts.totalRefundedAmount) || 0).div(100));
    }
    haveFees() {
        var _this_invoiceDTO_data_refunded_amounts, _this_invoiceDTO_data_refunded;
        return ((_this_invoiceDTO_data_refunded = this.invoiceDTO.data.refunded) === null || _this_invoiceDTO_data_refunded === void 0 ? void 0 : (_this_invoiceDTO_data_refunded_amounts = _this_invoiceDTO_data_refunded.amounts) === null || _this_invoiceDTO_data_refunded_amounts === void 0 ? void 0 : _this_invoiceDTO_data_refunded_amounts.refundedFeesAmount) || 0 > 0;
    }
    isFullRefund() {
        var _this_invoiceDTO_data_refunded;
        // before partial refund was introduced, we had a single refund amount which we took from the invoice total amount
        if (!((_this_invoiceDTO_data_refunded = this.invoiceDTO.data.refunded) === null || _this_invoiceDTO_data_refunded === void 0 ? void 0 : _this_invoiceDTO_data_refunded.amounts)) return true;
        return new Decimal(this.invoiceDTO.data.refunded.amounts.refundedAmount).div(100).eq(this.totalAmount().amount());
    }
    attachmentsAddedInfo() {
        const data = this.invoiceDTO.data.invoiceAttachmentsAdded;
        if (data == null) {
            throw new Error('Missing attachments added data');
        }
        const { userName, userEmail } = this.extractUpdatedBy(data.updatedBy);
        return {
            displayNames: data.displayNames,
            userName,
            userEmail
        };
    }
    attachmentRemovedInfo() {
        const data = this.invoiceDTO.data.invoiceAttachmentRemoved;
        if (data == null) {
            throw new Error('Missing attachment removed data');
        }
        const { userName, userEmail } = this.extractUpdatedBy(data.updatedBy);
        return {
            displayName: data.displayName,
            userName,
            userEmail
        };
    }
    adHocNotifyType() {
        const data = this.invoiceDTO.data.adHocIssued;
        if (data == null) {
            throw new Error('Missing adHocIssued data');
        }
        return data.notifyType;
    }
    adHocScheduledDate() {
        const data = this.invoiceDTO.data.adHocScheduled;
        if (data == null) {
            throw new Error('Missing adHocScheduled data');
        }
        return formatDateLongOrdinal(data.scheduledDate);
    }
    adHocLegalTermsDocument() {
        var _this_invoiceDTO_data_adHocApproved;
        return (_this_invoiceDTO_data_adHocApproved = this.invoiceDTO.data.adHocApproved) === null || _this_invoiceDTO_data_adHocApproved === void 0 ? void 0 : _this_invoiceDTO_data_adHocApproved.legalTermsDocument;
    }
    adHocSharedByLinkInfo() {
        const data = this.invoiceDTO.data.adHocSharedByLink;
        if (data == null) {
            throw new Error('Missing adHocSharedByLink data');
        }
        return {
            vendorName: data.vendorName,
            vendorEmail: data.vendorEmail
        };
    }
    extractUpdatedBy(updatedBy) {
        return {
            userName: updatedBy.userName,
            userEmail: updatedBy.userEmail
        };
    }
    constructor(dto){
        super(dto), _define_property(this, "invoiceDTO", void 0), _define_property(this, "totalAmountAsMoney", void 0);
        this.invoiceDTO = dto;
        this.totalAmountAsMoney = MoneyModel.fromDTO(dto.data.invoice.totalAmount);
    }
}
export class ActivityLogSentNotificationEntryModel extends ActivityLogEntryModel {
    state() {
        return this.sentNotificationDTO.state;
    }
    data() {
        return this.sentNotificationDTO.data;
    }
    isInvoiceKind() {
        return isInvoiceNotification(this.sentNotificationDTO);
    }
    invoiceData() {
        return isInvoiceNotification(this.sentNotificationDTO) ? this.sentNotificationDTO.data : null;
    }
    invoiceId() {
        return isInvoiceNotification(this.sentNotificationDTO) ? this.sentNotificationDTO.data.invoiceId : null;
    }
    invoiceNumber() {
        return isInvoiceNotification(this.sentNotificationDTO) ? this.sentNotificationDTO.data.invoiceNumber : null;
    }
    firstDelivery() {
        return this.sentNotificationDTO.data.deliveries[0];
    }
    deliveries() {
        return this.sentNotificationDTO.data.deliveries;
    }
    constructor(dto){
        super(dto), _define_property(this, "sentNotificationDTO", void 0);
        this.sentNotificationDTO = dto;
    }
}
export class ActivityLogBusinessEntryModel extends ActivityLogEntryModel {
    static make(dto) {
        switch(dto.data.type){
            case "businessNameChanged":
                return new ActivityLogBusinessNameChangedEntryModel(dto);
            case "customerNameChangedBySeller":
                return new ActivityLogCustomerNameChangedBySellerEntryModel(dto);
            case "teamMemberAdded":
                return new ActivityLogTeamMemberAddedEntryModel(dto);
            case "teamMemberAddedBySeller":
                return new ActivityLogTeamMemberAddedBySellerEntryModel(dto);
            case "teamMemberRemoved":
                return new ActivityLogTeamMemberRemovedEntryModel(dto);
            case "teamMemberRemovedBySeller":
                return new ActivityLogTeamMemberRemovedBySellerEntryModel(dto);
            case "teamMemberRoleChanged":
                return new ActivityLogTeamMemberRoleChangedEntryModel(dto);
            case "teamMemberRoleChangedBySeller":
                return new ActivityLogTeamMemberRoleChangedBySellerEntryModel(dto);
            case "customerBusinessCreated":
                return new ActivityLogCustomerBusinessCreatedEntryModel(dto);
            case "customerAddressChanged":
                return new ActivityLogCustomerAddressChangedEntryModel(dto);
        }
    }
    state() {
        return this.businessDTO.state;
    }
    businessData() {
        return this.businessDTO.data;
    }
    businessId() {
        return this.businessDTO.data.value.businessId;
    }
    isBusinessNameChanged() {
        return this.businessDTO.data.type === "businessNameChanged" || this.businessDTO.data.type === "customerNameChangedBySeller";
    }
    isTeamMemberAdded() {
        return this.businessDTO.data.type === "teamMemberAdded" || this.businessDTO.data.type === "teamMemberAddedBySeller";
    }
    isCustomerBusinessCreated() {
        return this.businessDTO.data.type === "customerBusinessCreated";
    }
    isBusinessNameChangedEntry() {
        return this.businessDTO.data.type === "businessNameChanged";
    }
    isCustomerNameChangedBySellerEntry() {
        return this.businessDTO.data.type === "customerNameChangedBySeller";
    }
    isTeamMemberAddedEntry() {
        return this.businessDTO.data.type === "teamMemberAdded";
    }
    isTeamMemberAddedBySellerEntry() {
        return this.businessDTO.data.type === "teamMemberAddedBySeller";
    }
    isTeamMemberRemovedEntry() {
        return this.businessDTO.data.type === "teamMemberRemoved";
    }
    isTeamMemberRemovedBySellerEntry() {
        return this.businessDTO.data.type === "teamMemberRemovedBySeller";
    }
    isTeamMemberRoleChangedEntry() {
        return this.businessDTO.data.type === "teamMemberRoleChanged";
    }
    isTeamMemberRoleChangedBySellerEntry() {
        return this.businessDTO.data.type === "teamMemberRoleChangedBySeller";
    }
    isCustomerBusinessCreatedEntry() {
        return this.businessDTO.data.type === "customerBusinessCreated";
    }
    isCustomerAddressChangedEntry() {
        return this.businessDTO.data.type === "customerAddressChanged";
    }
    acceptBusinessEntry(visitor) {
        if (this.isBusinessNameChangedEntry()) {
            return visitor.whenBusinessNameChanged(this);
        } else if (this.isCustomerNameChangedBySellerEntry()) {
            return visitor.whenCustomerNameChangedBySeller(this);
        } else if (this.isTeamMemberAddedEntry()) {
            return visitor.whenTeamMemberAdded(this);
        } else if (this.isTeamMemberAddedBySellerEntry()) {
            return visitor.whenTeamMemberAddedBySeller(this);
        } else if (this.isTeamMemberRemovedEntry()) {
            return visitor.whenTeamMemberRemoved(this);
        } else if (this.isTeamMemberRemovedBySellerEntry()) {
            return visitor.whenTeamMemberRemovedBySeller(this);
        } else if (this.isTeamMemberRoleChangedEntry()) {
            return visitor.whenTeamMemberRoleChanged(this);
        } else if (this.isTeamMemberRoleChangedBySellerEntry()) {
            return visitor.whenTeamMemberRoleChangedBySeller(this);
        } else if (this.isCustomerBusinessCreatedEntry()) {
            return visitor.whenCustomerBusinessCreated(this);
        } else if (this.isCustomerAddressChangedEntry()) {
            return visitor.whenCustomerAddressChanged(this);
        } else {
            const err = new Error("Unknown business entry type ".concat(this.businessDTO.data.type));
            sendErrReport(err);
            throw err;
        }
    }
    constructor(dto){
        super(dto), _define_property(this, "businessDTO", void 0);
        this.businessDTO = dto;
    }
}
export class ActivityLogBusinessNameChangedEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    oldName() {
        return this.data.value.oldName;
    }
    newName() {
        return this.data.value.newName;
    }
    updatedBy() {
        return this.data.value.updatedBy;
    }
}
export class ActivityLogCustomerNameChangedBySellerEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    oldName() {
        return this.data.value.oldName;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    newName() {
        return this.data.value.newName;
    }
    vendorName() {
        return this.data.value.vendorName;
    }
    updatedBy() {
        return this.data.value.updatedBy;
    }
}
export class ActivityLogCustomerAddressChangedEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    newAddress() {
        return this.data.value.newAddress;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
}
export class ActivityLogTeamMemberAddedEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    addedMemberName() {
        return this.data.value.addedMemberName;
    }
    addedMemberEmail() {
        return this.data.value.addedMemberEmail;
    }
    addedMemberRole() {
        return this.data.value.addedMemberRole;
    }
    updatedBy() {
        return this.data.value.updatedBy;
    }
}
export class ActivityLogTeamMemberAddedBySellerEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    addedMemberName() {
        return this.data.value.addedMemberName;
    }
    addedMemberEmail() {
        return this.data.value.addedMemberEmail;
    }
    addedMemberRole() {
        return this.data.value.addedMemberRole;
    }
    vendorName() {
        return this.data.value.vendorName;
    }
    updatedBy() {
        return this.data.value.updatedBy;
    }
}
export class ActivityLogTeamMemberRemovedEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    removedMemberName() {
        return this.data.value.removedMemberName;
    }
    removedMemberEmail() {
        return this.data.value.removedMemberEmail;
    }
}
export class ActivityLogTeamMemberRemovedBySellerEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    removedMemberName() {
        return this.data.value.removedMemberName;
    }
    removedMemberEmail() {
        return this.data.value.removedMemberEmail;
    }
    vendorName() {
        return this.data.value.vendorName;
    }
    updatedBy() {
        return this.data.value.updatedBy;
    }
}
export class ActivityLogTeamMemberRoleChangedEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    memberName() {
        return this.data.value.memberName;
    }
    memberEmail() {
        return this.data.value.memberEmail;
    }
    oldRole() {
        return this.data.value.oldRole;
    }
    newRole() {
        return this.data.value.newRole;
    }
    updatedBy() {
        return this.data.value.updatedBy;
    }
}
export class ActivityLogTeamMemberRoleChangedBySellerEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    updatedByName() {
        return this.data.value.updatedBy.userName;
    }
    updatedByEmail() {
        return this.data.value.updatedBy.userEmail;
    }
    memberName() {
        return this.data.value.memberName;
    }
    memberEmail() {
        return this.data.value.memberEmail;
    }
    oldRole() {
        return this.data.value.oldRole;
    }
    newRole() {
        return this.data.value.newRole;
    }
    vendorName() {
        return this.data.value.vendorName;
    }
}
export class ActivityLogCustomerBusinessCreatedEntryModel extends ActivityLogBusinessEntryModel {
    get data() {
        return this.businessDTO.data;
    }
    businessName() {
        return this.data.value.businessName;
    }
    vendorId() {
        return this.data.value.vendorId;
    }
    vendorName() {
        return this.data.value.vendorName;
    }
    createdBy() {
        return this.data.value.createdBy;
    }
    createdByName() {
        return this.data.value.createdBy.userName;
    }
    createdByEmail() {
        return this.data.value.createdBy.userEmail;
    }
}
